import { useEffect, useState } from 'react';
import {
  Form, Input, Button, Card, Space, Divider, message, Row, Col, Spin,
} from 'antd';
import { SaveOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/shared';
import { akimatApi } from '../../api/akimat';

const { TextArea } = Input;

export default function AkimatDirectory() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await akimatApi.getDirectory();
        form.setFieldsValue(data);
      } catch {
        message.error('Не удалось загрузить справочник');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue(true);
      await akimatApi.putDirectory(values);
      message.success('Справочник акимата сохранён');
    } catch {
      message.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  }

  return (
    <div>
      <PageHeader
        title="Справочник акимата"
        subtitle="Аким, заместители, отделы и сёла. Правится здесь — приложение и Айлана берут отсюда."
        extra={
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
            Сохранить
          </Button>
        }
      />

      <Form form={form} layout="vertical">
        <Card title="Аким города Аксай" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Должность" name={['city_aksai', 'title']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="ФИО акима" name={['city_aksai', 'akim']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Телефон" name={['city_aksai', 'phone']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Заместитель" name={['city_aksai', 'deputy']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Адрес / аппарат" name={['city_aksai', 'office']}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Аким Бурлинского района" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Должность" name={['district_burlin', 'title']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="ФИО акима" name={['district_burlin', 'akim']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Телефон" name={['district_burlin', 'phone']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Email" name={['district_burlin', 'email']}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Руководитель аппарата</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="ФИО" name={['district_burlin', 'apparatus_head', 'name']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="Роль" name={['district_burlin', 'apparatus_head', 'role']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Телефон" name={['district_burlin', 'apparatus_head', 'phone']}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Заместители акима района</Divider>
          <Form.List name={['district_burlin', 'deputies']}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item name={[field.name, 'name']} style={{ width: 240, marginBottom: 0 }}>
                      <Input placeholder="ФИО" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'curates']} style={{ width: 360, marginBottom: 0 }}>
                      <Input placeholder="Что курирует" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'phone']} style={{ width: 160, marginBottom: 0 }}>
                      <Input placeholder="Телефон" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                  Добавить зама
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Отделы акимата" style={{ marginBottom: 16 }}>
          <Form.List name="departments">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item name={[field.name, 'name']} style={{ width: 380, marginBottom: 0 }}>
                      <Input placeholder="Название отдела" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'head']} style={{ width: 240, marginBottom: 0 }}>
                      <Input placeholder="Руководитель" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'phone']} style={{ width: 160, marginBottom: 0 }}>
                      <Input placeholder="Телефон" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                  Добавить отдел
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Сельские округа" style={{ marginBottom: 16 }}>
          <Form.List name="village_councils">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item name={[field.name, 'name']} style={{ width: 280, marginBottom: 0 }}>
                      <Input placeholder="Название округа" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'phone']} style={{ width: 180, marginBottom: 0 }}>
                      <Input placeholder="Телефон" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                  Добавить округ
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Прочее" style={{ marginBottom: 16 }}>
          <Form.Item label="Актуальность (напр. «актуально на 2026-07-08»)" name="verified">
            <Input />
          </Form.Item>
          <Form.Item label="Заметка для Айланы (как отвечать)" name="note">
            <TextArea rows={4} />
          </Form.Item>
        </Card>

        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
          Сохранить справочник
        </Button>
      </Form>
    </div>
  );
}
